/**
 * MKS-393 — the size-chart manager on `/hr/merch-sizes`.
 *
 * One row per chart slot, always all six: a slot with no chart is a row with
 * an Upload button, not a missing row, so HR can see at a glance which of the
 * six they still owe people. T-shirt and hoodie take two each because
 * women's measurements differ from men's, and /profile opens the one matching
 * the cut a person picked. Uploading over an existing chart replaces it (the
 * backend deletes the old object), which is why the button reads "Replace"
 * once a chart is there.
 *
 * Admin and HR reach this page (`HRRoute`), and the two write endpoints are
 * `requireHR` on their own — the UI is not the guard.
 */
import { useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  DocumentIcon,
  PhotoIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { SizeChartLink } from '../profile/SizeChartLink';
import {
  useDeleteMerchSizeChart,
  useMerchSizeCharts,
  useUploadMerchSizeChart,
} from '../../hooks/useMerchSizeCharts';
import {
  MERCH_CHART_ACCEPT,
  MERCH_CHART_ITEMS,
  MERCH_CHART_ITEM_LABELS,
  MERCH_CHART_MAX_BYTES,
  opensInNewTab,
  type MerchChartItem,
} from '../../constants/merchSizeCharts';

const MAX_MIB = Math.round(MERCH_CHART_MAX_BYTES / (1024 * 1024));

export function MerchSizeChartsCard() {
  const { data: charts, isLoading } = useMerchSizeCharts();
  const upload = useUploadMerchSizeChart();
  const remove = useDeleteMerchSizeChart();
  // One ref per item: a single shared input would need the item threaded
  // through a piece of state that the file dialog's async return could race.
  const inputs = useRef<Partial<Record<MerchChartItem, HTMLInputElement | null>>>({});
  const [busyItem, setBusyItem] = useState<MerchChartItem | null>(null);

  async function handleFile(item: MerchChartItem, file: File | undefined) {
    if (!file) return;
    setBusyItem(item);
    try {
      await upload.mutateAsync({ item, file });
    } catch {
      // The hook's error toast already said so.
    } finally {
      setBusyItem(null);
      // Clear the input so picking the SAME file again still fires `change`.
      const input = inputs.current[item];
      if (input) input.value = '';
    }
  }

  async function handleRemove(item: MerchChartItem) {
    if (!window.confirm(`Remove the ${MERCH_CHART_ITEM_LABELS[item]} size chart?`)) return;
    setBusyItem(item);
    try {
      await remove.mutateAsync(item);
    } catch {
      // Toasted by the hook.
    } finally {
      setBusyItem(null);
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Size charts</h2>
        <p className="text-sm text-gray-500 mt-1">
          One chart per slot, shown to everyone as a “Size chart” link next to that size on
          their profile; the T-shirt and hoodie links follow the cut the person picked. PNG,
          JPG or PDF, up to {MAX_MIB} MB.
        </p>
      </div>

      <ul className="divide-y divide-gray-100">
        {MERCH_CHART_ITEMS.map((item) => {
          const chart = charts?.find((candidate) => candidate.item === item);
          const busy = busyItem === item;

          return (
            <li key={item} className="flex flex-wrap items-center gap-3 py-3">
              <span className="w-36 text-sm font-medium text-gray-900">
                {MERCH_CHART_ITEM_LABELS[item]}
              </span>

              <span className="flex-1 min-w-[200px] flex items-center gap-2 text-sm text-gray-500">
                {isLoading ? (
                  <span className="text-gray-400">Loading…</span>
                ) : chart ? (
                  <>
                    {opensInNewTab(chart.contentType) ? (
                      <DocumentIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                      <PhotoIcon className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="truncate" title={chart.fileName}>
                      {chart.fileName}
                    </span>
                    <SizeChartLink item={item} charts={charts} />
                  </>
                ) : (
                  <span className="text-gray-400">No chart uploaded</span>
                )}
              </span>

              <input
                ref={(element) => {
                  inputs.current[item] = element;
                }}
                type="file"
                accept={MERCH_CHART_ACCEPT}
                className="hidden"
                aria-label={`${MERCH_CHART_ITEM_LABELS[item]} size chart file`}
                data-testid={`size-chart-input-${item}`}
                onChange={(event) => void handleFile(item, event.target.files?.[0])}
              />

              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => inputs.current[item]?.click()}
                className="flex items-center gap-2"
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                {chart ? 'Replace' : 'Upload'}
              </Button>

              {chart && (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => void handleRemove(item)}
                  className="flex items-center gap-2 text-red-600"
                >
                  <TrashIcon className="h-4 w-4" />
                  Remove
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
